import { RefreshOutlined } from '@mui/icons-material';
import { Box, Chip, SxProps, Tooltip, Typography, styled } from '@mui/material';
import React, { useMemo } from 'react';
import { useTheme } from 'styled-components';
import { DEVICE_STATUS } from '../../../constants/originalState';

const StyledIcon = styled(Box)(
  ({ styleConfig }: { styleConfig: { fontSize: string; color: string; marginTop?: string } }) => ({
    svg: {
      fontSize: `${styleConfig?.fontSize}px` || 'unset',
      color: styleConfig?.color || 'unset',
      marginTop: `${styleConfig?.marginTop}px` || 'unset',
    },
  })
);

export interface StatusChipProps {
  assetEyebrow: string;
  status?: string;
  assetStatus?: string;
  isShowPrimaryLabel?: boolean;
  locale?: Record<string, any>;
  style?: SxProps;
}

const StatusChip = (props: StatusChipProps) => {
  const { assetEyebrow, status, assetStatus, isShowPrimaryLabel, locale, style } = props;

  const theme = useTheme();

  const textAssetFunction = locale?.commonStatus?.[assetStatus!]
    ? ` - ${locale?.commonStatus[assetStatus!]}`
    : '';

  const isInactiveStatus = assetEyebrow === DEVICE_STATUS.INACTIVE;

  const iconEyeBrow = useMemo(() => {
    return status === DEVICE_STATUS.ELIGIBLE_FOR_REFRESH ? (
      <StyledIcon
        styleConfig={{
          fontSize: theme.uptimeFontSizes.desktop.fz5 as unknown as string,
          color: isInactiveStatus
            ? theme.uptimeColors.deviceCardEyebrow.deviceCard.color
            : theme.uptimeColors.mainTheme.primary
        }}
      >
        <Box display="flex">
          <RefreshOutlined />
        </Box>
      </StyledIcon>
    ) : null;
  }, [isInactiveStatus, status, theme]);

  const eyeBrowText = useMemo(() => {
    const isEligibleForRefresh = status === DEVICE_STATUS.ELIGIBLE_FOR_REFRESH;
    const assetEyebrowText: string = !!assetEyebrow
      ? locale?.commonStatus?.[assetEyebrow] || assetEyebrow
      : locale?.commonStatus?.[DEVICE_STATUS?.IN_USE];

    if (isShowPrimaryLabel && isEligibleForRefresh) {
      return `${locale?.readyForRefresh}${textAssetFunction}`;
    }

    if (isShowPrimaryLabel && !isEligibleForRefresh) {
      return `${assetEyebrowText}${textAssetFunction}`;
    }

    if (!isShowPrimaryLabel && isEligibleForRefresh) {
      return `${locale?.readyForRefresh} - ${assetEyebrowText}`;
    }

    if (!isShowPrimaryLabel && !isEligibleForRefresh) {
      return `${assetEyebrowText}`;
    }

    return '';
  }, [status, assetEyebrow, locale, isShowPrimaryLabel, textAssetFunction]);

  const chipStyles = useMemo(() => {
    const color = isInactiveStatus
      ? theme.uptimeColors.deviceCardEyebrow.deviceCard.color
      : theme.uptimeColors.deviceCardEyebrow.inUse.color;

    const background = isInactiveStatus
      ? theme.uptimeColors.deviceCardEyebrow.deviceCard.background
      : theme.uptimeColors.deviceCardEyebrow.inUse.background;
    return { color, background };
  }, [isInactiveStatus, theme]);

  return (
    <Chip
      label={
        <Box display="flex" alignItems="center" gap="4px">
          {iconEyeBrow}
          <Tooltip title={eyeBrowText.toUpperCase()} arrow placement="bottom">
            <Typography
              fontSize="12px"
              fontWeight={600}
              fontFamily={theme.uptimeFonts.primary}
              textOverflow="ellipsis"
              whiteSpace="nowrap"
              overflow="hidden"
            >
              {eyeBrowText.toUpperCase()}
            </Typography>
          </Tooltip>
        </Box>
      }
      sx={{ maxWidth: 'fit-content', ...chipStyles, ...style }}
    />
  );
};

export default StatusChip;
